import { Button } from '@/components'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Download } from 'lucide-react'
import type { FormEvent, PropsWithChildren, ReactNode } from 'react'

interface ExportCardProps extends PropsWithChildren {
  className?: string

  title: string
  description?: ReactNode
  headerRight?: ReactNode
  loading?: boolean

  submitLabel?: string
  submitDisabled?: boolean
  footerLeft?: ReactNode

  onSubmit(): void
}

export const ExportCard: React.FC<ExportCardProps> = ({
  children,
  className,

  title,
  description,
  headerRight,
  loading = false,

  submitLabel,
  submitDisabled = false,
  footerLeft,

  onSubmit,
}) => {
  const translations = useTranslate({
    export: 'common.export',
  })

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (submitDisabled || loading) return
    onSubmit()
  }

  return (
    <form
      className={cn(
        'overflow-hidden rounded-2xl bg-white shadow-card',
        className
      )}
      onSubmit={handleSubmit}
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-stone-200 px-5 py-4 sm:px-6">
        <div className="min-w-0 space-y-0.5">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-stone-900">{title}</h3>
            {loading && <Spinner className="size-4 text-stone-500" />}
          </div>
          {!!description && (
            <p className="text-sm text-stone-500">{description}</p>
          )}
        </div>

        {headerRight}
      </header>

      <div className="grid gap-x-4 gap-y-5 px-5 py-5 sm:px-6 md:grid-cols-2">
        {children}
      </div>

      <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-stone-200 px-5 py-4 sm:px-6">
        {footerLeft && <div className="mr-auto">{footerLeft}</div>}

        <Button
          type="submit"
          size="lg"
          className="w-full sm:w-auto"
          disabled={submitDisabled || loading}
        >
          <Download />
          {submitLabel ?? translations.export}
        </Button>
      </footer>
    </form>
  )
}
